import { useState } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  FileText,
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Download,
  Eye,
  Edit,
  Trash2,
  Clock,
  AlertTriangle,
  CheckCircle,
  Archive,
  Award,
  FileCheck,
  Shield,
  BookOpen,
  Scale,
  FolderOpen,
  Calendar,
  RefreshCw,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format, differenceInDays, isPast } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  useLegalDocumentsList,
  useLegalDocumentCategories,
  useLegalDocumentsStats,
  useDeleteLegalDocument,
  useArchiveLegalDocument,
} from '@/hooks/useLegalDocuments';
import {
  documentTypeLabels,
  documentStatusLabels,
  documentStatusColors,
  confidentialityLabels,
  confidentialityColors,
  documentTypes,
  documentStatuses,
  type DocumentType,
  type DocumentStatus,
  type LegalDocument,
} from '@/schemas/legalDocumentSchemas';
import { DocumentUploadDialog } from '@/components/LegalDocuments/DocumentUploadDialog';
import { DocumentViewerDialog } from '@/components/LegalDocuments/DocumentViewerDialog';
import { FolderManager } from '@/components/LegalDocuments/FolderManager';

// Iconos por categoría
const categoryIcons: Record<string, any> = {
  CERT: Award,
  LIC: FileCheck,
  POL: FileText,
  PROC: FileText,
  CONT: FileText,
  SEG: Shield,
  AUD: Search,
  MAN: BookOpen,
  REG: Archive,
  NORM: Scale,
};

export default function LegalDocuments() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<DocumentType | 'all'>('all');
  const [selectedStatus, setSelectedStatus] = useState<DocumentStatus | 'all'>('all');
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('all');

  // Dialogs
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [viewerDialogOpen, setViewerDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<LegalDocument | null>(null);
  const [editingDocument, setEditingDocument] = useState<LegalDocument | null>(null);

  // Queries
  const { data: documents = [], isLoading, refetch } = useLegalDocumentsList({
    categoryId: selectedCategory !== 'all' ? selectedCategory : undefined,
    folderId: selectedFolderId,
    status: selectedStatus !== 'all' ? selectedStatus : undefined,
    documentType: selectedType !== 'all' ? selectedType : undefined,
    search: searchTerm || undefined,
  });
  const { data: categories = [] } = useLegalDocumentCategories();
  const stats = useLegalDocumentsStats();

  // Mutations
  const deleteDocument = useDeleteLegalDocument();
  const archiveDocument = useArchiveLegalDocument();

  // Filtrar documentos por tab
  const filteredDocuments = documents.filter((doc) => {
    if (activeTab === 'all') return true;
    if (activeTab === 'expiring') {
      if (!doc.expiration_date) return false;
      const daysUntilExpiration = differenceInDays(new Date(doc.expiration_date), new Date());
      return daysUntilExpiration >= 0 && daysUntilExpiration <= 30;
    }
    if (activeTab === 'expired') {
      return doc.status === 'expired' || (doc.expiration_date && isPast(new Date(doc.expiration_date)));
    }
    if (activeTab === 'pending') {
      return doc.status === 'pending_approval';
    }
    return true;
  });

  const handleViewDocument = (doc: LegalDocument) => {
    setSelectedDocument(doc);
    setViewerDialogOpen(true);
  };

  const handleEditDocument = (doc: LegalDocument) => {
    setEditingDocument(doc);
    setUploadDialogOpen(true);
  };

  const handleDeleteDocument = async (id: string) => {
    if (confirm('¿Está seguro de eliminar este documento?')) {
      await deleteDocument.mutateAsync(id);
    }
  };

  const handleArchiveDocument = async (id: string) => {
    if (confirm('¿Está seguro de archivar este documento?')) {
      await archiveDocument.mutateAsync(id);
    }
  };

  const getExpirationBadge = (doc: LegalDocument) => {
    if (!doc.expiration_date) return null;
    const daysUntil = differenceInDays(new Date(doc.expiration_date), new Date());

    if (daysUntil < 0) {
      return (
        <Badge variant="destructive" className="gap-1">
          <AlertTriangle className="w-3 h-3" />
          Vencido
        </Badge>
      );
    }
    if (daysUntil <= 30) {
      return (
        <Badge variant="outline" className="gap-1 border-yellow-500 text-yellow-700">
          <Clock className="w-3 h-3" />
          {daysUntil} días
        </Badge>
      );
    }
    return null;
  };

  return (
    <DashboardLayout>
      <div className="flex h-[calc(100vh-4rem)]">
        {/* Folder Sidebar */}
        <div className="w-64 border-r bg-muted/30 hidden lg:block">
          <FolderManager
            selectedFolderId={selectedFolderId}
            onSelectFolder={setSelectedFolderId}
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold">Documentación Legal</h1>
                <p className="text-muted-foreground mt-1">
                  Gestión de documentos legales, certificaciones y auditorías
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Actualizar
                </Button>
                <Button onClick={() => { setEditingDocument(null); setUploadDialogOpen(true); }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Documento
                </Button>
              </div>
            </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <FileText className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.active}</p>
                  <p className="text-xs text-muted-foreground">Activos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-500/10">
                  <Clock className="w-5 h-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.pendingApproval}</p>
                  <p className="text-xs text-muted-foreground">Pendientes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-500/10">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.expiringInThirtyDays}</p>
                  <p className="text-xs text-muted-foreground">Por vencer</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-500/10">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.expired}</p>
                  <p className="text-xs text-muted-foreground">Vencidos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gray-500/10">
                  <Edit className="w-5 h-5 text-gray-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.draft}</p>
                  <p className="text-xs text-muted-foreground">Borradores</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar documentos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full md:w-[200px]">
                  <FolderOpen className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Categoría" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las categorías</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedType} onValueChange={(v) => setSelectedType(v as DocumentType | 'all')}>
                <SelectTrigger className="w-full md:w-[180px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los tipos</SelectItem>
                  {documentTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {documentTypeLabels[type]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedStatus} onValueChange={(v) => setSelectedStatus(v as DocumentStatus | 'all')}>
                <SelectTrigger className="w-full md:w-[180px]">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  {documentStatuses.map((status) => (
                    <SelectItem key={status} value={status}>
                      {documentStatusLabels[status]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Documents Table */}
        <Card>
          <CardHeader className="pb-3">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">Todos ({documents.length})</TabsTrigger>
                <TabsTrigger value="expiring" className="gap-2">
                  <Clock className="w-4 h-4" />
                  Por vencer ({stats.expiringInThirtyDays})
                </TabsTrigger>
                <TabsTrigger value="expired" className="gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Vencidos ({stats.expired})
                </TabsTrigger>
                <TabsTrigger value="pending">Pendientes ({stats.pendingApproval})</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : filteredDocuments.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <FileText className="w-12 h-12 mb-4 opacity-50" />
                <p>No se encontraron documentos</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => { setEditingDocument(null); setUploadDialogOpen(true); }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar documento
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Documento</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Categoría</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Versión</TableHead>
                    <TableHead>Vigencia</TableHead>
                    <TableHead>Vencimiento</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDocuments.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-muted">
                            <FileText className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="font-medium">{doc.title}</p>
                            {doc.code && (
                              <p className="text-xs text-muted-foreground">{doc.code}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {documentTypeLabels[doc.document_type]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {doc.category?.name || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={cn(documentStatusColors[doc.status])}>
                          {documentStatusLabels[doc.status]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{doc.version}</span>
                      </TableCell>
                      <TableCell>
                        {doc.effective_date ? (
                          <span className="text-sm">
                            {format(new Date(doc.effective_date), 'dd/MM/yyyy')}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {doc.expiration_date ? (
                            <>
                              <span className="text-sm">
                                {format(new Date(doc.expiration_date), 'dd/MM/yyyy')}
                              </span>
                              {getExpirationBadge(doc)}
                            </>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewDocument(doc)}>
                              <Eye className="w-4 h-4 mr-2" />
                              Ver detalles
                            </DropdownMenuItem>
                            {doc.file_url && (
                              <DropdownMenuItem asChild>
                                <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                                  <Download className="w-4 h-4 mr-2" />
                                  Descargar
                                </a>
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleEditDocument(doc)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleArchiveDocument(doc.id)}>
                              <Archive className="w-4 h-4 mr-2" />
                              Archivar
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleDeleteDocument(doc.id)}
                              className="text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
          </div>
        </div>
      </div>

      {/* Upload Dialog */}
      <DocumentUploadDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        document={editingDocument}
        categories={categories}
        selectedFolderId={selectedFolderId}
      />

      {/* Viewer Dialog */}
      <DocumentViewerDialog
        open={viewerDialogOpen}
        onOpenChange={setViewerDialogOpen}
        document={selectedDocument}
      />
    </DashboardLayout>
  );
}
